import React, { useEffect, useState } from 'react';
import {
  getGoogleClassroomCourseStudents,
  GoogleClassroomCourse,
  GoogleClassroomStudent,
} from '../services/GoogleClassroomStudentImport.service';
import { Toast } from '../../../components/Common/Toast';
import BackButton from '../../../components/Common/BackButton';
import Loader from '../../../components/Common/Loader';

interface GoogleClassroomStudentSelectorProps {
  course: GoogleClassroomCourse;
  onStudentsSelect: (studentIds: string[]) => void;
  onBack: () => void;
  onClose: () => void;
  googleClassroomImportLoading?: boolean;
}

const GoogleClassroomStudentSelector: React.FC<GoogleClassroomStudentSelectorProps> = ({
  course,
  onStudentsSelect,
  onBack,
  onClose,
  googleClassroomImportLoading = false,
}) => {
  const [students, setStudents] = useState<GoogleClassroomStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getGoogleClassroomCourseStudents(course.id, '');

        if (data) {
          setStudents(data);
        } else {
          setError('Failed to fetch students from Google Classroom');
        }
      } catch (error) {
        setError('Failed to fetch students from Google Classroom');
        console.error('Error fetching students:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [course.id]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedStudents(new Set(students.map((student) => student.id)));
      setSelectAll(true);
    } else {
      setSelectedStudents(new Set());
      setSelectAll(false);
    }
  };

  const handleStudentSelect = (studentId: string, checked: boolean) => {
    const newSelected = new Set(selectedStudents);
    if (checked) {
      newSelected.add(studentId);
    } else {
      newSelected.delete(studentId);
    }
    setSelectedStudents(newSelected);
    setSelectAll(newSelected.size === students.length);
  };

  const handleImport = () => {
    if (selectedStudents.size === 0) {
      Toast('Please select at least one student to import', true);
      return;
    }
    onStudentsSelect(Array.from(selectedStudents));
  };

  if (loading) {
    return (
      <div className="relative sm:w-[360px] w-full">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-storyblue"></div>
          <span className="ml-3 text-lg">Loading students...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative sm:w-[360px] w-full">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mt-4">Error</h3>
          <p className="text-sm text-gray-500 mt-2">{error}</p>
          <div className="mt-4 flex space-x-3">
            <button
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Go Back
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative sm:w-[360px] w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-storyblue">Select Students</h3>
      </div>

      {/* Course Info */}
      {/*<div className="mb-4 p-3 bg-blue-50 rounded-lg">*/}
      {/*  <h4 className="text-sm font-medium text-blue-900">{course.name}</h4>*/}
      {/*  {course.section && <p className="text-sm text-blue-700">Section: {course.section}</p>}*/}
      {/*  <p className="text-xs text-blue-600 mt-1">*/}
      {/*    {students.length} student{students.length !== 1 ? 's' : ''} available*/}
      {/*  </p>*/}
      {/*</div>*/}

      {/* Select All */}
      {students.length > 0 && (
        <div className="mb-4 pl-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">Select all students ({students.length})</span>
          </label>
        </div>
      )}

      {/* Students List */}
      <div className="max-h-64 overflow-y-auto">
        {students.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No students found</h3>
            <p className="mt-1 text-sm text-gray-500">This course doesn't have any students enrolled.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {students.map((student) => (
              <label
                key={student.id}
                className="flex items-center p-3 border border-transparent rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedStudents.has(student.id)}
                  onChange={(e) => handleStudentSelect(student.id, e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div className="ml-3 flex-1 min-w-0">
                  <div className="flex items-center">
                    {student.profile.photoUrl ? (
                      <img
                        src={student.profile.photoUrl}
                        alt={student.profile.name.fullName}
                        className="h-8 w-8 rounded-full mr-3"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center mr-3">
                        <span className="text-sm font-medium text-gray-600">
                          {student.profile.name.givenName.charAt(0)}
                          {student.profile.name.familyName.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{student.profile.name.fullName}</p>
                      <p className="text-sm text-gray-500 truncate">{student.profile.emailAddress}</p>
                    </div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 flex justify-between">
        <div>
          <BackButton onClick={onBack}></BackButton>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleImport}
            disabled={selectedStudents.size === 0 || googleClassroomImportLoading}
            className={`px-6 py-2 h-9  font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center justify-center text-sm sm:text-base w-full sm:w-auto ${
              selectedStudents.size > 0
                ? 'bg-secondaryStoryAdditionalLightBlueText text-whiteText'
                : 'opacity-50 cursor-not-allowed'
            } ${googleClassroomImportLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
          >
            Import ({selectedStudents.size})
            <Loader loader={googleClassroomImportLoading} dimentions="w-8 h-8" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoogleClassroomStudentSelector;
